import { ColoredChip } from '@components/colored-elements';
import { EntityNameColumn } from '@components/entity-name-column';
import { EntityIcon } from '@components/entity-type-icon';
import { TimeAgo } from '@components/time-ago';
import { useDefaultImage, windowObj } from '@lib/common';
import {
  CompanyDetail, CompanyMappingDetail
} from '@lib/company-mapping';
import { Avatar, FormControlLabel, IconButton, Radio, Tooltip, Typography } from '@material-ui/core';
import { amber, blue, brown, cyan, deepOrange, deepPurple, green, grey, indigo, lime, orange, pink, teal, yellow } from '@material-ui/core/colors';
import CancelIcon from '@material-ui/icons/Cancel';
import CategoryIcon from '@material-ui/icons/Category';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DomainIcon from '@material-ui/icons/Domain';
import EventIcon from '@material-ui/icons/Event';
import LanguageIcon from '@material-ui/icons/Language';
import OfflinePinIcon from '@material-ui/icons/OfflinePin';
import { AvatarGroup, Skeleton } from '@material-ui/lab';
import { isValid } from 'date-fns';
import { camelCase, startCase, uniq } from 'lodash';
import React, { PropsWithChildren, useMemo, useState } from 'react';
import { CellProps } from 'react-table';
import { useCompanyLoader } from 'services/company-dataloader.service';
import { useContentLoader } from 'services/content-dataloader.service';
import { usePersonLoader } from 'services/person-dataloader.service';
import { $eq } from '../filters';
import { FilterForm, useFilterFormActions } from '../filters/types/filter-form';
import { ContentDetail } from '@lib/content';
type EnumObject = { label: React.ReactNode; value: any };
type EnumMap = EnumObject[];

/**
 * Convert a string based enum into objects
 * and redable labels.
 *
 * @param enum_ The enum to convert
 */
export const createEnumMap = (enum_: Record<string, any>, retainSpecialChars = false): EnumMap =>
  Object.values(enum_).map(value => ({
    label: retainSpecialChars ? value : startCase(camelCase(value)),
    value,
  }));

/**
 * Render a enum map to Select options.
 * @param enumMap The Enum map
 */
export const EnumsToOptions = (enumMap: EnumMap) => (
  <>
    {enumMap.map(v => (
      <option key={v.value} value={v.value}>
        {v.label}
      </option>
    ))}
  </>
);

/**
 * Override equals operator to show readable
 * values of enums.
 */
export const enumOperatorOverride = {
  [$eq.key]: {
    ChipLabel({ label, value }) {
      return (
        <>
          {label} is {startCase(camelCase(value))}
        </>
      );
    },
  },
};

/**
 * Create a small subtitle, used for Entity Names.
 */
function SmallSubTitle({ name, icon, display = 'block' }) {
  return (
    <span style={{ color: 'inherit', fontSize: 11, display, alignItems: 'center' }}>
      {icon}&nbsp;{name}
    </span>
  );
}

const skeleton = <Skeleton style={{ display: 'block' }} variant="text" animation="wave" />;
/**
 * Gets the company details from the server and shows the
 * country and the primary type of the company.
 */
function ShowCompanyDetails({ id }: { id: string }) {
  const { loading, data: company, error } = useCompanyLoader(id);
  const companyType = company && company.company_types[0]?.name;
  return (
    <>
      {loading && (
        <>
          {skeleton}
          {skeleton}
        </>
      )}
      {!loading && company && (
        <>
          <SmallSubTitle
            name={company.hqcountry}
            icon={<LanguageIcon style={{ fontSize: '1em' }} />}
          />
          {companyType && (
            <SmallSubTitle
              name={companyType}
              icon={<CategoryIcon style={{ fontSize: '1em' }} color="primary" />}
            />
          )}
        </>
      )}
    </>
  );
}


export const CellForContentLanguages = ({
  value,
  row,
}: {
  value: string;
  row: { [key: string]: ContentDetail };
}) => {
  const max_limit: number = 1;
  const listOfContentLanguages = row.original.lang_names
    ? uniq(row.original.lang_names.split(',').map(x => x.trim()))
    : [];

  const title = listOfContentLanguages.slice(max_limit).join(', ');
  const sliced = listOfContentLanguages.slice(0, max_limit);
  return row.original.lang_names ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
      {sliced.map((eachLanguage, index) => {
        return (
          <div key={index} style={{ display: 'flex' }}>
            <Tooltip title={eachLanguage}>
              <ColoredChip
                colored={deepOrange[800]}
                size="small"
                // variant="outlined"
                label={eachLanguage}
              />
            </Tooltip>
            {index === sliced.length - 1 && listOfContentLanguages.length - max_limit > 0 ? (
              <Tooltip title={title}>
                <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${
                  listOfContentLanguages.length - max_limit
                }`}</span>
              </Tooltip>
            ) : null}
          </div>
        );
      })}
    </div>
  ) : null;
};

export const CellForReleaseYear = ({
  value,
  row,
}: {
  value: string;
  row: { [key: string]: ContentDetail };
}) => {
  const max_limit: number = 1;
  const listOfReleaseYear = row.original.release_date
    ? uniq(row.original.release_date.split(',').map(x => x.trim()))
    : [];
  const r_year = parseInt(listOfReleaseYear[0]);

  let year: string | number | undefined = row.original.release_year;
  if (year === undefined || year === null || year === "") {
    year = r_year;
  }
  // If year is not a valid number, set to blank
  if (year === undefined || year === null || year === "" || isNaN(Number(year))) {
    return null;
  }
  year = String(year);

  return year ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
      <Tooltip title={year}>
        <ColoredChip
          colored={pink[800]}
          size="small"
          // variant="outlined"
          label={year}
        />
      </Tooltip>
    </div>
  ) : null;
};

export default function ClipBoard({ parentValue, icon, display = 'block', value }) {
  const [isHovering, setHovering] = useState(false);
  const [isClick, setClick] = useState(false);

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => {
        setClick(false);
        setTimeout(() => {
          setHovering(false);
        }, 5000);
      }}
    >
      <span>{parentValue}</span>
      {isHovering && (
        <Tooltip
          title={isClick ? 'Copied!!' : 'Copy the Vitrina Id'}
          onClick={() => {
            navigator.clipboard.writeText(value);
            setClick(true);
          }}
        >
          <span style={{ cursor: 'pointer' }}>{icon}</span>
        </Tooltip>
      )}
    </span>
  );
}

/**
 * Gets the person details from the server.
 */
function ShowPersonDetails({ id }: { id: string }) {
  const { loading, data: person, error } = usePersonLoader(id);
  const personDepartment = person && person.role_id;
  return (
    <>
      {loading && (
        <>
          <Skeleton style={{ display: 'block' }} variant="text" animation="wave" />
        </>
      )}
      {!loading && person && (
        <>
          <SmallSubTitle
            name={personDepartment}
            icon={<DomainIcon style={{ fontSize: '1em' }} />}
          />
        </>
      )}
    </>
  );
}

function ShowContentDetails({ id }: { id: string }) {
  const { loading, data: content, error } = useContentLoader(id);
  const finished = !loading && content;
  const release_date = finished && new Date(content.release_date);
  const year = finished && isValid(release_date) ? release_date.getFullYear() : 'NA';

  return (
    <>
      {loading && (
        <>
          <Skeleton style={{ display: 'block' }} variant="text" animation="wave" />
        </>
      )}
      {finished && (
        <>
          {content.content_type && (
            <>
              <SmallSubTitle
                display="inline-flex"
                name={content.content_type}
                icon={<CategoryIcon style={{ fontSize: '1em' }} color="primary" />}
              />
              &nbsp;
            </>
          )}
          {content.lang_names && (
            <>
              <SmallSubTitle
                display="inline-flex"
                name={content.lang_names}
                icon={<LanguageIcon style={{ fontSize: '1em' }} />}
              />
              &nbsp;
            </>
          )}
          {year && (
            <SmallSubTitle
              display="inline-flex"
              name={year}
              icon={<EventIcon style={{ fontSize: '1em' }} />}
            />
          )}
        </>
      )}
      {error && <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />}
    </>
  );
}

export const SingleBuyerSellerCell = ({
  name: entityName,
  vtrId: entityVtrId,
  // type: entityType= 'COMPANY',
  white = false,
}: {
  name: React.ReactNode;
  vtrId?: string;
  // type: string;
  white?: boolean;
  // contentType?: any; //ignore
}) => {
  const secondaryProps = useMemo(() => (white ? { color: 'inherit' } : {}), [white]);
  let Details: any;
  const srcList = useMemo(() => {
    return [
      `${windowObj.RAZZLE_S3_BUCKET_URL}/${entityVtrId}.png`,
      '/images/company_default.svg',
    ].filter(e => e);
  }, []);

  const { src: fallbackedSource } = useDefaultImage({
    srcList,
    defaultSrc: '/images/company_default.svg',
    useSuspense: false,
  });

  Details = ShowCompanyDetails;
  return entityName || entityVtrId ? (
    <>
      <EntityNameColumn
        name={entityName}
        type={'COMPANY'}
        vtrId={entityVtrId}
        secondary={
          entityVtrId ? (
            Details ? (
              <Details id={entityVtrId} />
            ) : (
              <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />
            )
          ) : null
        }
        avatarImg={fallbackedSource}
        secondaryTypographyProps={secondaryProps}
      />
    </>
  ) : null;
};

export const SingleContentCell = ({
  name,
  vtrId = null,
  white = false,
  contentType = null,
}: {
  name: React.ReactNode;
  vtrId?: string;
  white?: boolean;
  contentType?: string;
}) => {
  const secondaryProps = useMemo(() => (white ? { color: 'inherit' } : {}), [white]);
  return name ? (
    <EntityNameColumn
      name={name}
      type="CONTENT"
      secondary={vtrId ? <ShowContentDetails id={vtrId} /> : null}
      secondaryTypographyProps={secondaryProps}
      overrideAvatar={contentType}
    />
  ) : null;
};

export const CellForMultiEntity =
  () => (props: PropsWithChildren<CellProps<CompanyMappingDetail, CompanyDetail[]>>) => {
    const entities = props.value ? (!Array.isArray(props.value) ? [props.value] : props.value) : [];
    const RenderComponent = SingleBuyerSellerCell;

    if (!entities.length) {
      return null;
    }

    const firstEntityName = entities[0].name;
    // const contentType = type === 'company1' ? getContentNameType(entities[0]) : null;

    return (
      <>
        {/* <p>hi425435</p> */}
        <RenderComponent
          name={<>{firstEntityName} </>}
          // contentType={contentType}
          // type={entities[0].type}
          vtrId={entities[0].vtr_id}
        />

        <AvatarGroup>
          {entities.slice(1).map((entity, idx) => (
            <Avatar key={`entity-${idx}`} style={smallAvatar}>
              <Tooltip
                title={
                  <RenderComponent
                    name={entity.name}
                    vtrId={entity.vtr_id}
                    // type={entity.type}
                    white
                  // contentType={getContentNameType(entity)}
                  />
                }>
                <EntityIcon noTooltip type={'COMPANY'} />
              </Tooltip>
            </Avatar>
          ))}
        </AvatarGroup>
      </>
    );
  };
const smallAvatar = { height: '1.1em', width: '1.1em' };




/**
 * Higher order function to create c
 * @param entity The entity to create object for.
 */
export const cellForEntity =
  (entity: 1 | 2 | '1' | '2') =>
    ({ row }) => {
      const entityName = row.original[`entity${entity}`];
      const entityVtrId = row.original[`entity${entity}_vtr_id`];
      const entityType = row.original[`entity${entity}_type`];
      return <SingleBuyerSellerCell name={entityName} vtrId={entityVtrId} />; //type={entityType}
    };
/**
 *  function to create cell for content
 * @param entity The entity to create object for.
 */
export const CellForContent = ({ row }) =>
  row.original.content ? (
    <SingleContentCell name={row.original.content} vtrId={row.original.content_vtr_id} />
  ) : null;

/**
 *  function to create cell for content
 * @param entity The entity to create object for.
 */
export const CellForDealSource = ({ row }) =>
  row.original.deal_source && (
    <p>{row.original.deal_source.deal_source_type + 'xyz'}</p>
  );


export const QuickTitle = ({ title, subTitle = 'Advanced Filters' }) => (
  <>
    <Typography variant="h5">{title}</Typography>
    <Typography variant="body2" color="textSecondary">
      {subTitle}
    </Typography>
    <br />
  </>
);

export const addTitleToFilter =
  (Filter: any, title?: string, subTitle = 'Advanced Filters') =>
    props => {
      const actualTitle = useMemo(
        () => title || props.column.render('Header'),
        [props.column.Header]
      );
      return (
        <>
          <QuickTitle title={actualTitle} subTitle={subTitle} />
          <Filter {...props} />
        </>
      );
    };
export const CellForServices = ({ value, row }) => {
  const max_limit: number = 1;
  // console.log(value);
  // if (!value) return null;
  const listOfServices = value && value ? uniq(value.split(';;').map(x => x.trim())) : [];

  const title = listOfServices.slice(max_limit).join(', ');
  const sliced = listOfServices.slice(0, max_limit);

  return row.original.service_name ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
      {sliced.map((eachServiceAka, index) => {
        return (
          <div key={index} style={{ display: 'flex' }}>
            <Tooltip title={eachServiceAka}>
              <ColoredChip
                colored={indigo[900]}
                size="small"
                // variant="outlined"
                label={eachServiceAka}
              />
            </Tooltip>
            {index === sliced.length - 1 && listOfServices.length - max_limit > 0 ? (
              <Tooltip title={title}>
                <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${listOfServices.length - max_limit
                  }`}</span>
              </Tooltip>
            ) : null}
          </div>
        );
      })}
    </div>
  ) : null;
};

export const CellForHQCountry = ({ value, row }) => {
  return (
    <Typography style={{ color: (row.original.company_active == 'Inactive') ? 'red' : '' }}>{value}</Typography>
  );
};

function BooleanFilterForm({ setRadioFilter, radioValue, column }) {
  const { handleInputClick, form, getRadioValue } = useFilterFormActions(
    radioValue,
    setRadioFilter
  );

  return (
    <>
      <FormControlLabel
        value="yes"
        control={<Radio />}
        label="Yes"
        name="$eq"
        inputRef={form.register}
      />
      <FormControlLabel
        value="no"
        control={<Radio />}
        label="No"
        name="$eq"
        inputRef={form.register}
      />
    </>
  );
}

function BooleanFilterFormBoolVal({ setRadioFilter, radioValue, column }) {
  const { handleInputClick, form, getRadioValue } = useFilterFormActions(
    radioValue,
    setRadioFilter
  );

  return (
    <>
      <FormControlLabel
        value="true"
        control={<Radio />}
        label="Yes"
        name="$eq"
        inputRef={form.register}
      />
      <FormControlLabel
        value="false"
        control={<Radio />}
        label="No"
        name="$eq"
        inputRef={form.register}
      />
    </>
  );
}

function YesAndNoFilterForm({ setRadioFilter, radioValue, column }) {
  const { handleInputClick, form, getRadioValue } = useFilterFormActions(
    radioValue,
    setRadioFilter
  );

  return (
    <>
      <FormControlLabel
        value="false"
        control={<Radio />}
        label="Yes"
        name="$empty"
        inputRef={form.register}
      />
      <FormControlLabel
        value="true"
        control={<Radio />}
        label="No"
        name="$empty"
        inputRef={form.register}
      />
    </>
  );
}

function CurrentlyWorkingFilter({ setRadioFilter, radioValue}) {
  const { form } = useFilterFormActions(
    radioValue,
    setRadioFilter
  );

  return (
    <>
      <FormControlLabel
        value="true"
        control={<Radio />}
        label="Yes"
        name="$eq"
        inputRef={form.register}
      />
      <FormControlLabel
        value="false"
        control={<Radio />}
        label="No"
        name="$eq"
        inputRef={form.register}
      />
      <FormControlLabel
        value="not_sure"
        control={<Radio />}
        label="Not Sure"
        name="$eq"
        inputRef={form.register}
      />
    </>
  );
}

export const CurrentlyWorkingBooleanFilters = props => (
  <FilterForm {...props} radioFilters={CurrentlyWorkingFilter} />
);

export const BooleanFilters = props => (
  <FilterForm {...props} radioFilters={BooleanFilterForm} />
);

export const BooleanFiltersWithBoolValues = props => (
  <FilterForm {...props} radioFilters={BooleanFilterFormBoolVal} />
);

export const YesAndNoFilters = props => (
  <FilterForm {...props} radioFilters={YesAndNoFilterForm} />
);

export const CellForRelationship = ({ value, row }) => {
  const max_limit: number = 1;
  // const relationship = row.original.relation_name.toUpperCase();
  const relationship = row.original.relation_name
  let color = ""
  if (relationship == 'Group Company') {
    color = pink[800]
  }
  else if (relationship == 'TV_CATALOG') {
    color = deepPurple[800]
  }
  else if (relationship == 'App Marketplace') {
    color = blue[800]
  }
  else if (relationship == 'Brand') {
    color = teal[800]
  }
  else if (relationship == 'Competition') {
    color = yellow[800]
  }
  else if (relationship == 'Operator') {
    color = brown[800]
  }
  else if (relationship == 'FAST Channel Aggregator') {
    color = green[800]
  }
  else if (relationship == 'Investor') {
    color = cyan[800]
  }
  else if (relationship == 'OTT_Catalog') {
    color = amber[800]
  }
  else if (relationship == 'Owner') {
    color = lime[800]
  }
  else if (relationship == 'Partner') {
    color = indigo[800]
  }
  else if (relationship == 'Platform') {
    color = grey[800]
  }
  else {
    color = orange[800]
  }

  return row.original.relation_name ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
      <Tooltip title={row.original.relation_name}>
        <ColoredChip
          colored={color}
          size="medium"
          label={row.original.relation_name}
          style={{ fontSize: 'medium' }}
        />
      </Tooltip>
    </div>
  ) : null;
};

export const CellForLastUpdatedAt = ({ value, row }) => {
  return <TimeAgo time={value ? new Date(value) : new Date(row.original.created_at)} breakLine />;
};


export const CellForPublished = ({ value, row }) => {
  const publish_status = 1;

  if (publish_status == 1) {
    return (
      (
        <Tooltip title="Published">
          <IconButton
            style={{ color: 'green' }}>
            <CheckCircleIcon style={{ fontSize: '1.5rem' }} />
          </IconButton>
        </Tooltip>
      )
    )
  }
  else {
    return (
      <Tooltip title="UnPublished">
        <IconButton style={{ color: 'red' }}>
          <CancelIcon style={{ fontSize: '1.5rem' }} />
        </IconButton>
      </Tooltip>
    )
  }

  // return <TimeAgo time={value ? new Date(value) : new Date(row.original.created_at)} breakLine />;
};


export const cellForUpdatedBy = ({ value, row }) => {
  const updated_by = (row.original.company2[0].updated_by);
  return (
    <Tooltip title={updated_by}>
      <ColoredChip
        colored={blue[500]}
        size="small"
        label={updated_by}
      />
    </Tooltip>

  )


  // return <TimeAgo time={value ? new Date(value) : new Date(row.original.created_at)} breakLine />;
};

export const AddNonLinkedEntityFilter = (Filter: any) => props => {
  return (
    <>
      <Filter {...props} addNonLinkedEntityFilter={true} />
    </>
  );
};

// utils/roleUtils.tsx

// roleApi should be imported from your API service file
import * as roleApi from '@api/role';

export const fetchRoles = async () => {
  try {
    const val = await roleApi.roleSearch().toPromise();
    return val;
  } catch (error: any) {
    console.error(`Error fetching roles: ${error.message || error}`, { variant: 'error' });
    return [];
  }
};

export const fetchSubRoles = async () => {
   
  try {
    const val = await roleApi.subRoleSearch().toPromise();
    return val;
  } catch (error: any) {
    console.error(`Error fetching subroles: ${error.message || error}`, { variant: 'error' });
    return {};
  }
};